import React, { useMemo, useState } from 'react';
import Box from '../../components/Common/Box';
import { NuitSeanceStats, NuitSeanceValeur, useLiveMarketPeriode } from '../../hooks/useLiveMarketPeriode';

/**
 * Marché live — onglet « Sur une période ».
 *
 * Décompose chaque journée du bulletin officiel en mouvement de nuit (veille →
 * ouverture) et mouvement de séance (ouverture → clôture).
 *
 * ⚠️ Rien ici n'est un signal. La « part de l'écart reprise » est une moyenne
 * passée, en partie mécanique ; l'écran l'écrit en toutes lettres et affiche
 * partout le nombre de séances sur lequel chaque chiffre repose.
 */

const COULEUR_HAUSSE = '#03543f';
const COULEUR_BAISSE = '#9b1c1c';

const PERIODES: { libelle: string; mois: number | null }[] = [
    { libelle: '1 mois', mois: 1 },
    { libelle: '3 mois', mois: 3 },
    { libelle: '1 an', mois: 12 },
    { libelle: '3 ans', mois: 36 },
    { libelle: '5 ans', mois: 60 },
    { libelle: 'Depuis 2015', mois: null },
];

const fmt = (v: number | null | undefined, decimales = 2): string =>
    v === null || v === undefined
        ? '—'
        : v.toLocaleString('fr-FR', { minimumFractionDigits: decimales, maximumFractionDigits: decimales });

const fmtSigne = (v: number | null | undefined, decimales = 2): string =>
    v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${fmt(v, decimales)} %`;

const fmtDate = (iso: string | null): string =>
    iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—';

const couleur = (v: number | null | undefined): string | undefined =>
    v === null || v === undefined || v === 0 ? undefined : v > 0 ? COULEUR_HAUSSE : COULEUR_BAISSE;

/** Part de l'écart d'ouverture reprise avant la clôture = −pente, en %. */
const partReprise = (s: NuitSeanceStats): number | null => (s.pente === null ? null : -s.pente * 100);

const th: React.CSSProperties = { color: 'white', padding: '12px 14px', textAlign: 'right', fontSize: '12px', whiteSpace: 'nowrap' };
const thGauche: React.CSSProperties = { ...th, textAlign: 'left' };
const td: React.CSSProperties = { padding: '10px 14px', textAlign: 'right', fontSize: '13px' };
const tdGauche: React.CSSProperties = { ...td, textAlign: 'left' };

type CleTri =
    | 'societe_symbole'
    | 'nb_lignes'
    | 'part_ouverture_inchangee_pct'
    | 'nuit_moyenne_pct'
    | 'seance_moyenne_pct'
    | 'reprise'
    | 'hausse'
    | 'baisse';

const COLONNES: { cle: CleTri; libelle: string; aide: string; gauche?: boolean }[] = [
    { cle: 'societe_symbole', libelle: 'Valeur', aide: 'Symbole', gauche: true },
    { cle: 'nb_lignes', libelle: 'Séances', aide: 'Séances où la valeur a échangé sur la période' },
    { cle: 'part_ouverture_inchangee_pct', libelle: 'Ouv. = veille', aide: 'Part des séances ouvertes exactement au cours de la veille' },
    { cle: 'nuit_moyenne_pct', libelle: 'Nuit moy.', aide: 'Nuit : de la clôture de la veille à l’ouverture du jour, en moyenne sur la période' },
    { cle: 'seance_moyenne_pct', libelle: 'Séance moy.', aide: 'Séance : de l’ouverture à la clôture du même jour, en moyenne sur la période' },
    { cle: 'reprise', libelle: 'Écart repris', aide: 'Part moyenne du mouvement de nuit annulée pendant la séance' },
    { cle: 'hausse', libelle: 'Après ouv. ≥ +2 %', aide: 'Mouvement de séance moyen après une ouverture en hausse d’au moins 2 %' },
    { cle: 'baisse', libelle: 'Après ouv. ≤ −2 %', aide: 'Mouvement de séance moyen après une ouverture en baisse d’au moins 2 %' },
];

const valeurTri = (v: NuitSeanceValeur, cle: CleTri): string | number | null => {
    switch (cle) {
        case 'societe_symbole':
            return v.societe_symbole;
        case 'reprise':
            return partReprise(v);
        case 'hausse':
            return v.apres_ouverture_en_hausse.seance_moyenne_pct;
        case 'baisse':
            return v.apres_ouverture_en_baisse.seance_moyenne_pct;
        default:
            return v[cle];
    }
};

const Tuile: React.FC<{ titre: string; valeur: string; detail?: string; teinte?: string }> = ({ titre, valeur, detail, teinte }) => (
    <div style={{ minWidth: '170px' }}>
        <div style={{ fontSize: '12px', color: '#64748b' }}>{titre}</div>
        <div style={{ fontSize: '20px', fontWeight: 700, color: teinte }}>{valeur}</div>
        {detail && <div style={{ fontSize: '12px', color: '#64748b' }}>{detail}</div>}
    </div>
);

const LiveMarketPeriode: React.FC = () => {
    const [mois, setMois] = useState<number | null>(12);
    const { marche, valeurs, meta, loading, error } = useLiveMarketPeriode(mois);
    const [tri, setTri] = useState<{ cle: CleTri; croissant: boolean }>({ cle: 'societe_symbole', croissant: true });

    const lignes = useMemo(() => {
        const sens = tri.croissant ? 1 : -1;
        return [...valeurs].sort((a, b) => {
            const va = valeurTri(a, tri.cle);
            const vb = valeurTri(b, tri.cle);
            // Une statistique non mesurable reste en fin de liste quel que soit le sens.
            if (va === null && vb === null) return a.societe_symbole.localeCompare(b.societe_symbole);
            if (va === null) return 1;
            if (vb === null) return -1;
            const ecart = typeof va === 'string' || typeof vb === 'string' ? String(va).localeCompare(String(vb), 'fr') : va - vb;
            return ecart !== 0 ? ecart * sens : a.societe_symbole.localeCompare(b.societe_symbole);
        });
    }, [valeurs, tri]);

    const reprise = marche ? partReprise(marche) : null;

    return (
        <>
            <Box>
                <div
                    className='box-title box-vertical-padding box-horizontal-padding'
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}
                >
                    <p style={{ margin: 0 }}>
                        Nuit et séance
                        {meta && ` · ${fmtDate(meta.premiere_seance)} au ${fmtDate(meta.derniere_seance)} · ${meta.nb_seances} séance(s)`}
                    </p>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {PERIODES.map((p) => (
                            <button
                                key={p.libelle}
                                className={mois === p.mois ? 'button button-purple' : 'button'}
                                onClick={() => setMois(p.mois)}
                                disabled={loading}
                            >
                                {p.libelle}
                            </button>
                        ))}
                    </div>
                </div>

                <div className='box-content box-horizontal-padding' style={{ padding: '0 20px 20px' }}>
                    <div
                        style={{
                            padding: '12px 16px',
                            borderRadius: '8px',
                            backgroundColor: '#eff6ff',
                            border: '1px solid #bfdbfe',
                            color: '#1e40af',
                            fontSize: '13px',
                            lineHeight: 1.5,
                            marginBottom: '16px',
                        }}
                    >
                        <strong>Constat sur le passé, pas une prévision.</strong> Chaque journée du bulletin officiel est coupée en
                        deux mouvements, la nuit et la séance, définis ci-dessous. La reprise de
                        l'écart d'ouverture est en partie mécanique : le cours d'ouverture entre dans les deux calculs, et le
                        bulletin ne dit pas combien de titres ont fait l'ouverture. Seules les séances où la valeur a échangé
                        sont comptées.
                    </div>

                    {/* Les trois termes de la vue, définis à l'écran : aucun ne va de soi. */}
                    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '16px', fontSize: '13px', lineHeight: 1.5 }}>
                        <div style={{ flex: '1 1 240px' }}>
                            <strong>Nuit</strong>
                            <div style={{ color: '#475569' }}>
                                Du cours de clôture de la veille au cours d'ouverture du jour. C'est ce que le cours a fait
                                pendant que le marché était fermé (week-end et jours fériés compris).
                            </div>
                        </div>
                        <div style={{ flex: '1 1 240px' }}>
                            <strong>Séance</strong>
                            <div style={{ color: '#475569' }}>
                                Du cours d'ouverture au cours de clôture du même jour. C'est ce que le cours a fait pendant
                                que le marché était ouvert.
                            </div>
                        </div>
                        <div style={{ flex: '1 1 240px' }}>
                            <strong>Écart repris</strong>
                            <div style={{ color: '#475569' }}>
                                Part du mouvement de nuit annulée pendant la séance. Exemple : veille 10 000 F, ouverture
                                10 300 F, clôture 10 180 F — nuit +3 %, séance −1,2 %, 40 % de l'écart repris.
                            </div>
                        </div>
                    </div>

                    {error && <p style={{ fontSize: '13px', color: COULEUR_BAISSE }}>{error}</p>}
                    {loading && <p>Calcul sur la période…</p>}

                    {!loading && marche && marche.nb_lignes > 0 && (
                        <div style={{ display: 'flex', gap: '28px', flexWrap: 'wrap' }}>
                            <Tuile
                                titre='Écart d’ouverture repris en séance'
                                valeur={reprise === null ? '—' : `${fmt(reprise, 0)} %`}
                                detail={
                                    meta
                                        ? `sens identique pour ${meta.nb_valeurs_pente_negative} valeur(s) sur ${meta.nb_valeurs_relation_mesurable} mesurables`
                                        : undefined
                                }
                            />
                            <Tuile
                                titre='Ouvertures au cours de la veille'
                                valeur={`${fmt(marche.part_ouverture_inchangee_pct, 1)} %`}
                                detail={`${marche.nb_lignes.toLocaleString('fr-FR')} séances × valeurs`}
                            />
                            <Tuile titre='Nuit, moyenne' valeur={fmtSigne(marche.nuit_moyenne_pct, 3)} teinte={couleur(marche.nuit_moyenne_pct)} />
                            <Tuile titre='Séance, moyenne' valeur={fmtSigne(marche.seance_moyenne_pct, 3)} teinte={couleur(marche.seance_moyenne_pct)} />
                            <Tuile
                                titre='Après une ouverture ≥ +2 %'
                                valeur={fmtSigne(marche.apres_ouverture_en_hausse.seance_moyenne_pct)}
                                detail={`${marche.apres_ouverture_en_hausse.nb.toLocaleString('fr-FR')} cas · écart moyen ${fmtSigne(marche.apres_ouverture_en_hausse.ecart_moyen_pct)}`}
                                teinte={couleur(marche.apres_ouverture_en_hausse.seance_moyenne_pct)}
                            />
                            <Tuile
                                titre='Après une ouverture ≤ −2 %'
                                valeur={fmtSigne(marche.apres_ouverture_en_baisse.seance_moyenne_pct)}
                                detail={`${marche.apres_ouverture_en_baisse.nb.toLocaleString('fr-FR')} cas · écart moyen ${fmtSigne(marche.apres_ouverture_en_baisse.ecart_moyen_pct)}`}
                                teinte={couleur(marche.apres_ouverture_en_baisse.seance_moyenne_pct)}
                            />
                        </div>
                    )}

                    {!loading && !error && marche && marche.nb_lignes === 0 && (
                        <p style={{ color: '#94a3b8' }}>Aucune séance avec échange sur cette période.</p>
                    )}
                </div>
            </Box>

            {!loading && lignes.length > 0 && (
                <Box>
                    <div className='box-title box-vertical-padding box-horizontal-padding'>
                        <p>Par valeur · {lignes.length} valeur(s)</p>
                    </div>
                    <div className='box-content box-horizontal-padding' style={{ padding: '0 20px 20px' }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table className='data-table' style={{ width: '100%' }}>
                                <thead>
                                    <tr style={{ background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)' }}>
                                        {COLONNES.map((c) => {
                                            const actif = tri.cle === c.cle;
                                            return (
                                                <th
                                                    key={c.cle}
                                                    title={c.aide}
                                                    aria-sort={actif ? (tri.croissant ? 'ascending' : 'descending') : 'none'}
                                                    onClick={() =>
                                                        setTri((t) =>
                                                            t.cle === c.cle
                                                                ? { cle: c.cle, croissant: !t.croissant }
                                                                : { cle: c.cle, croissant: c.cle === 'societe_symbole' }
                                                        )
                                                    }
                                                    style={{ ...(c.gauche ? thGauche : th), cursor: 'pointer', userSelect: 'none' }}
                                                >
                                                    {c.libelle}
                                                    <span style={{ marginLeft: '4px', opacity: actif ? 1 : 0.35 }}>
                                                        {actif ? (tri.croissant ? '▲' : '▼') : '↕'}
                                                    </span>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {lignes.map((v, i) => {
                                        const r = partReprise(v);
                                        return (
                                            <tr key={v.societe_symbole} style={{ backgroundColor: i % 2 === 0 ? 'transparent' : 'var(--bg-hover)' }}>
                                                <td style={{ ...tdGauche, fontWeight: 600 }} title={v.nom_societe ?? undefined}>
                                                    {v.societe_symbole}
                                                </td>
                                                <td style={{ ...td, color: '#64748b' }}>{v.nb_lignes}</td>
                                                <td style={td}>{fmt(v.part_ouverture_inchangee_pct, 1)} %</td>
                                                <td style={{ ...td, color: couleur(v.nuit_moyenne_pct) }}>{fmtSigne(v.nuit_moyenne_pct)}</td>
                                                <td style={{ ...td, color: couleur(v.seance_moyenne_pct) }}>{fmtSigne(v.seance_moyenne_pct)}</td>
                                                <td style={{ ...td, fontWeight: 600 }}>{r === null ? '—' : `${fmt(r, 0)} %`}</td>
                                                <td style={{ ...td, color: couleur(v.apres_ouverture_en_hausse.seance_moyenne_pct) }}>
                                                    {fmtSigne(v.apres_ouverture_en_hausse.seance_moyenne_pct)}
                                                    <span style={{ color: '#94a3b8' }}> ({v.apres_ouverture_en_hausse.nb})</span>
                                                </td>
                                                <td style={{ ...td, color: couleur(v.apres_ouverture_en_baisse.seance_moyenne_pct) }}>
                                                    {fmtSigne(v.apres_ouverture_en_baisse.seance_moyenne_pct)}
                                                    <span style={{ color: '#94a3b8' }}> ({v.apres_ouverture_en_baisse.nb})</span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <p style={{ marginTop: '10px', fontSize: '12px', color: '#64748b' }}>
                            « Écart repris » n'est affiché qu'à partir de {meta?.min_lignes_relation ?? 30} séances. Entre parenthèses :
                            le nombre de cas. Les mouvements de plus de 10 % (opérations sur titres, défauts de données) sont écartés.
                        </p>
                    </div>
                </Box>
            )}
        </>
    );
};

export default LiveMarketPeriode;
